import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Home, ArrowLeft, ChevronRight, ChevronLeft, Check, X, Shield,
  Building2, BarChart3, CreditCard, Gem, Target, TrendingUp, Users, Award, Star,
  Briefcase, Globe, Layers, Landmark, Wallet, Heart, Zap, Lock, Eye, Compass, Info,
  Handshake, DollarSign, Download, FileImage, FileText, Share2, Link2, ExternalLink,
  Copy,
  // Luxury & lifestyle
  Waves, Wine, UtensilsCrossed, Dumbbell, Flower2, Sparkles, Bed, Bath, Car,
  BatteryCharging, Wifi, Camera, Sun, TreePine, Crown, Trophy, ConciergeBell,
  KeyRound, Music, Store, Coffee, Plane, BellRing, Sofa, Tv, ShoppingBag,
  Snowflake, Bot, Phone, Mail, Clock, Calendar, Sailboat, Anchor, Mountain,
  PartyPopper, Cake, Lightbulb, Palette, Gamepad2, Baby, Dog,
  Pill, Cross, Plus, Stethoscope,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand tokens (mirrors template using brand colors)
const GREEN = '#0a1a14';
const GREEN_DEEP = '#081610';
const GOLD = '#D4AF37';
const GOLD_DEEP = '#B8941E';
const CREAM = '#f4eddc';
const CREAM_DARK = '#0a1a14';

const ICON_MAP = {
  // Core
  'building': Building2, 'map-pin': MapPin, 'bar-chart': BarChart3, 'credit-card': CreditCard,
  'gem': Gem, 'target': Target, 'trending-up': TrendingUp, 'users': Users, 'award': Award,
  'star': Star, 'briefcase': Briefcase, 'globe': Globe, 'layers': Layers, 'landmark': Landmark,
  'wallet': Wallet, 'heart': Heart, 'zap': Zap, 'shield': Shield, 'eye': Eye, 'compass': Compass,
  'home': Home, 'check': Check, 'lock': Lock, 'info': Info, 'pound': DollarSign,
  'handshake': Handshake, 'dollar': DollarSign,
  // Luxury amenities
  'pool': Waves, 'waves': Waves,
  'bar': Wine, 'wine': Wine, 'cocktail': Wine,
  'restaurant': UtensilsCrossed, 'dining': UtensilsCrossed, 'utensils': UtensilsCrossed,
  'gym': Dumbbell, 'fitness': Dumbbell, 'dumbbell': Dumbbell,
  'spa': Flower2, 'wellness': Flower2, 'flower': Flower2,
  'sparkles': Sparkles, 'beauty': Sparkles,
  'bed': Bed, 'bedroom': Bed,
  'bath': Bath, 'bathroom': Bath,
  'parking': Car, 'car': Car,
  'ev-charging': BatteryCharging, 'battery': BatteryCharging,
  'wifi': Wifi, 'internet': Wifi,
  'security': Camera, 'cctv': Camera, 'camera': Camera,
  'sunlight': Sun, 'sun': Sun, 'rooftop': Sun,
  'garden': TreePine, 'tree': TreePine, 'nature': TreePine,
  'crown': Crown, 'premium': Crown, 'luxury': Crown,
  'trophy': Trophy, 'achievement': Trophy,
  'concierge': ConciergeBell, 'bell': ConciergeBell,
  'keyless': KeyRound, 'smart-lock': KeyRound, 'key': KeyRound,
  'music': Music, 'audio': Music,
  'retail': Store, 'shop': Store, 'store': Store,
  'cafe': Coffee, 'coffee': Coffee,
  'airport': Plane, 'travel': Plane, 'plane': Plane,
  'notification': BellRing,
  'lounge': Sofa, 'sofa': Sofa, 'living': Sofa,
  'tv': Tv, 'entertainment': Tv,
  'shopping': ShoppingBag, 'mall': ShoppingBag,
  'climate': Snowflake, 'ac': Snowflake, 'cooling': Snowflake,
  'smart-home': Bot, 'automation': Bot,
  'phone': Phone, 'contact': Phone,
  'mail': Mail, 'email': Mail,
  'clock': Clock, 'time': Clock,
  'calendar': Calendar, 'schedule': Calendar,
  'marina': Sailboat, 'boat': Sailboat, 'sailing': Sailboat,
  'anchor': Anchor,
  'mountain': Mountain, 'view': Mountain,
  'events': PartyPopper, 'celebration': PartyPopper,
  'cake': Cake, 'patisserie': Cake,
  'innovation': Lightbulb, 'idea': Lightbulb,
  'design': Palette, 'art': Palette,
  'gaming': Gamepad2, 'esports': Gamepad2,
  'kids': Baby, 'nursery': Baby,
  'pet': Dog, 'dog': Dog,
  // Healthcare / Pharmacy / Medical
  'pharmacy': Pill, 'pill': Pill, 'medication': Pill,
  'medical': Cross, 'cross': Cross, 'first-aid': Cross,
  'hospital': Plus, 'clinic': Plus,
  'doctor': Stethoscope, 'healthcare': Stethoscope, 'stethoscope': Stethoscope,
};
const getIcon = (name, className, color) => {
  const I = ICON_MAP[name] || Info;
  // Pass color via inline style — html2canvas honours inline styles but ignores
  // <style> blocks injected mid-DOM, so we avoid those entirely.
  return <I className={className} style={color ? { color } : undefined} />;
};

const EXPERIENCE_LEVELS = [
  { id: 'first-time', label: 'First-Time Investor', desc: 'New to property investment, seeking guidance on my first deal' },
  { id: 'beginner', label: 'Beginner', desc: '1-3 completed deals, building my portfolio and knowledge base' },
  { id: 'experienced', label: 'Experienced', desc: '4-10 deals completed, established strategy and professional network' },
  { id: 'seasoned', label: 'Seasoned Professional', desc: '10+ deals, sophisticated investor with a proven track record' },
];
const STRATEGIES = ['Rent to SA', 'Buy Refurbish Refinance (BRR)', 'HMO', 'Flip', 'Buy to Let', 'Commercial', 'Development', 'Off-Plan', 'Other'];
const TIMELINES = ['Immediately', 'Within 1 month', '1-3 months', '3-6 months', '6+ months'];

// Common international dialling codes (ordered by frequency for our market)
const COUNTRY_CODES = [
  { code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: '+1', flag: '🇺🇸', name: 'United States' },
  { code: '+1', flag: '🇨🇦', name: 'Canada' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+852', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+64', flag: '🇳🇿', name: 'New Zealand' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+34', flag: '🇪🇸', name: 'Spain' },
  { code: '+39', flag: '🇮🇹', name: 'Italy' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+31', flag: '🇳🇱', name: 'Netherlands' },
  { code: '+32', flag: '🇧🇪', name: 'Belgium' },
  { code: '+41', flag: '🇨🇭', name: 'Switzerland' },
  { code: '+46', flag: '🇸🇪', name: 'Sweden' },
  { code: '+47', flag: '🇳🇴', name: 'Norway' },
  { code: '+45', flag: '🇩🇰', name: 'Denmark' },
  { code: '+358', flag: '🇫🇮', name: 'Finland' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+30', flag: '🇬🇷', name: 'Greece' },
  { code: '+48', flag: '🇵🇱', name: 'Poland' },
  { code: '+420', flag: '🇨🇿', name: 'Czech Republic' },
  { code: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: '+90', flag: '🇹🇷', name: 'Turkey' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: '+212', flag: '🇲🇦', name: 'Morocco' },
  { code: '+225', flag: '🇨🇮', name: "Côte d'Ivoire" },
  { code: '+221', flag: '🇸🇳', name: 'Senegal' },
  { code: '+255', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+256', flag: '🇺🇬', name: 'Uganda' },
  { code: '+250', flag: '🇷🇼', name: 'Rwanda' },
  { code: '+260', flag: '🇿🇲', name: 'Zambia' },
  { code: '+263', flag: '🇿🇼', name: 'Zimbabwe' },
  { code: '+251', flag: '🇪🇹', name: 'Ethiopia' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: '+82', flag: '🇰🇷', name: 'South Korea' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+62', flag: '🇮🇩', name: 'Indonesia' },
  { code: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: '+66', flag: '🇹🇭', name: 'Thailand' },
  { code: '+84', flag: '🇻🇳', name: 'Vietnam' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+94', flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: '+52', flag: '🇲🇽', name: 'Mexico' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: '+56', flag: '🇨🇱', name: 'Chile' },
  { code: '+7', flag: '🇷🇺', name: 'Russia' },
];

// Renders a string highlighting any `|` separators in gold (used for hero title + positioning)
const renderWithGoldPipes = (text) => {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split('|');
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part.trim()}
      {i < parts.length - 1 && (
        <span style={{ color: GOLD, fontWeight: 700 }}>{' | '}</span>
      )}
    </React.Fragment>
  ));
};

// ============== 4-STEP ENQUIRY MODAL ==============
const EnquiryModal = ({ deal, onClose }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', phone_country: '+44', email: '',
    experience_level: '', preferred_strategy: '', preferred_strategy_other: '',
    funds_available: '',
    invest_timeline: '', mailing_list: false, consent: false,
  });

  const canNext = () => {
    if (step === 1) return form.first_name.trim() && form.last_name.trim();
    if (step === 2) return form.phone.trim();
    if (step === 3) return form.email.trim() && form.email.includes('@');
    if (step === 4) {
      if (!form.experience_level || !form.consent) return false;
      if (form.preferred_strategy === 'Other' && !form.preferred_strategy_other.trim()) return false;
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!canNext()) return;
    setSubmitting(true);
    try {
      const fullPhone = `${form.phone_country || ''} ${form.phone || ''}`.trim();
      const finalStrategy = form.preferred_strategy === 'Other' && form.preferred_strategy_other.trim()
        ? `Other: ${form.preferred_strategy_other.trim()}`
        : form.preferred_strategy;
      const payload = {
        ...form,
        phone: fullPhone,
        preferred_strategy: finalStrategy,
        deal_id: deal.id,
        deal_slug: deal.slug,
      };
      const res = await fetch(`${API_URL}/api/public/deals/${deal.slug}/enquiry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) { setSubmitted(true); toast.success('Enquiry submitted'); }
      else toast.error('Failed to submit');
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  if (submitted) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-[#0a1a14] border border-[#D4AF37]/30 rounded-2xl max-w-lg w-full p-10 text-center relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8 text-[#D4AF37]" /></div>
        <h3 className="text-2xl font-bold text-white mb-3">Thank You, {form.first_name}</h3>
        <p className="text-white/60 text-sm leading-relaxed mb-6">Your investor enquiry has been received. A member of our deal sourcing team will be in touch within 24 hours.</p>
        <button onClick={onClose} className="bg-[#D4AF37] text-[#0a1a14] font-semibold px-8 py-3 rounded-lg hover:bg-[#B8941E] transition-colors">Close</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-[#0a1a14] border border-[#D4AF37]/20 rounded-2xl max-w-xl w-full relative overflow-hidden" data-testid="enquiry-modal">
        <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8941E] px-6 py-4 flex items-center justify-between">
          <h3 className="text-[#0a1a14] font-bold text-lg">Secure This Opportunity</h3>
          <button onClick={onClose} className="text-[#0a1a14]/50 hover:text-[#0a1a14]"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 pt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/50 text-xs font-medium">Step {step} of 4</span>
            <span className="text-[#D4AF37] text-xs font-medium">{Math.round((step/4)*100)}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B8941E] rounded-full transition-all duration-500" style={{ width: `${(step/4)*100}%` }} />
          </div>
        </div>
        <div className="px-6 py-6 min-h-[280px]">
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-white/40 text-sm">Let us know who you are</p>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-white/60 text-xs block mb-1.5">First Name <span className="text-[#D4AF37]">*</span></label>
                  <input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" placeholder="First name" data-testid="enquiry-first-name" /></div>
                <div><label className="text-white/60 text-xs block mb-1.5">Last Name <span className="text-[#D4AF37]">*</span></label>
                  <input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" placeholder="Last name" data-testid="enquiry-last-name" /></div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-white/40 text-sm">How can we reach you?</p>
              <div><label className="text-white/60 text-xs block mb-1.5">Phone / Mobile <span className="text-[#D4AF37]">*</span></label>
                <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden focus-within:border-[#D4AF37]/50">
                  <select
                    value={`${form.phone_country}|${COUNTRY_CODES.findIndex(c => c.code === form.phone_country)}`}
                    onChange={e => {
                      const [code] = e.target.value.split('|');
                      setForm({ ...form, phone_country: code });
                    }}
                    className="bg-[#0a1a14] text-white text-sm px-3 py-3 border-r border-white/10 focus:outline-none cursor-pointer max-w-[110px]"
                    data-testid="enquiry-phone-country"
                  >
                    {COUNTRY_CODES.map((c, i) => (
                      <option key={`${c.code}-${i}`} value={`${c.code}|${i}`} className="bg-[#0a1a14]">
                        {c.flag} {c.code} {c.name}
                      </option>
                    ))}
                  </select>
                  <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="flex-1 bg-transparent px-4 py-3 text-white text-sm focus:outline-none" placeholder="7700 000000" data-testid="enquiry-phone" />
                </div></div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5">
              <p className="text-white/40 text-sm">Where should we send the deal briefing?</p>
              <div><label className="text-white/60 text-xs block mb-1.5">Email Address <span className="text-[#D4AF37]">*</span></label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" placeholder="you@example.com" data-testid="enquiry-email" /></div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5 max-h-[360px] overflow-y-auto pr-1">
              <p className="text-white/40 text-sm">Tell us about your investment profile</p>
              <div><label className="text-white/60 text-xs block mb-2">Experience Level <span className="text-[#D4AF37]">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPERIENCE_LEVELS.map(level => (
                    <button key={level.id} onClick={() => setForm({...form, experience_level: level.id})}
                      className={`text-left p-3 rounded-lg border transition-all ${form.experience_level === level.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                      data-testid={`exp-level-${level.id}`}>
                      <span className={`text-xs font-semibold block ${form.experience_level === level.id ? 'text-[#D4AF37]' : 'text-white/80'}`}>{level.label}</span>
                      <span className="text-white/30 text-[10px] leading-tight block mt-1">{level.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-white/60 text-xs block mb-1.5">Preferred Strategy</label>
                  <select value={form.preferred_strategy} onChange={e => setForm({...form, preferred_strategy: e.target.value, preferred_strategy_other: e.target.value === 'Other' ? form.preferred_strategy_other : ''})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none appearance-none" data-testid="enquiry-strategy">
                    <option value="" className="bg-[#0a1a14]">Select...</option>
                    {STRATEGIES.map(s => <option key={s} value={s} className="bg-[#0a1a14]">{s}</option>)}
                  </select></div>
                <div><label className="text-white/60 text-xs block mb-1.5">Funds Available</label>
                  <input value={form.funds_available} onChange={e => setForm({...form, funds_available: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none" placeholder="Amount" data-testid="enquiry-funds" /></div>
              </div>
              {form.preferred_strategy === 'Other' && (
                <div data-testid="enquiry-strategy-other-wrap">
                  <label className="text-white/60 text-xs block mb-1.5">Tell us your strategy <span className="text-[#D4AF37]">*</span></label>
                  <input
                    value={form.preferred_strategy_other}
                    onChange={e => setForm({...form, preferred_strategy_other: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                    placeholder="e.g. Co-living, Rent-to-Rent, Land Banking…"
                    data-testid="enquiry-strategy-other"
                  />
                </div>
              )}
              <div><label className="text-white/60 text-xs block mb-1.5">How soon to invest?</label>
                <select value={form.invest_timeline} onChange={e => setForm({...form, invest_timeline: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none appearance-none" data-testid="enquiry-timeline">
                  <option value="" className="bg-[#0a1a14]">Select...</option>
                  {TIMELINES.map(t => <option key={t} value={t} className="bg-[#0a1a14]">{t}</option>)}
                </select></div>
              <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={form.mailing_list} onChange={e => setForm({...form, mailing_list: e.target.checked})} className="mt-0.5 accent-[#D4AF37]" />
                <span className="text-white/40 text-xs">Send me future deal alerts from Black Star PI</span></label>
              <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={form.consent} onChange={e => setForm({...form, consent: e.target.checked})} className="mt-0.5 accent-[#D4AF37]" data-testid="enquiry-consent" />
                <span className="text-white/40 text-xs">I consent to my data being processed per the <a href="/privacy-policy" target="_blank" className="text-[#D4AF37] underline">privacy policy</a> <span className="text-[#D4AF37]">*</span></span></label>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex items-center justify-between">
          {step > 1 ? <button onClick={() => setStep(step - 1)} className="flex items-center gap-1.5 text-white/40 hover:text-white text-sm" data-testid="enquiry-prev"><ChevronLeft className="w-4 h-4" /> Back</button> : <div />}
          {step < 4 ? (
            <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
              className={`flex items-center gap-1.5 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${canNext() ? 'bg-[#D4AF37] text-[#0a1a14] hover:bg-[#B8941E]' : 'bg-white/5 text-white/20 cursor-not-allowed'}`}
              data-testid="enquiry-next">Next <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={handleSubmit} disabled={!canNext() || submitting}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${canNext() && !submitting ? 'bg-[#D4AF37] text-[#0a1a14] hover:bg-[#B8941E]' : 'bg-white/5 text-white/20 cursor-not-allowed'}`}
              data-testid="enquiry-submit">{submitting ? 'Submitting...' : 'Submit Enquiry'}</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== MAIN DEAL PAGE ==============
const DealPage = () => {
  const { slug } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'png' | 'pdf' | null
  const [previewModal, setPreviewModal] = useState(null); // { type, url, filename }
  const [lightboxIdx, setLightboxIdx] = useState(null); // gallery lightbox open index
  const [shareOpen, setShareOpen] = useState(false);
  const [captionModal, setCaptionModal] = useState(null); // 'linkedin' | 'x' | 'whatsapp' | 'telegram' | 'email' | null
  // Admin-only toolbar gating: WhatsApp + Share buttons are private (admin distribution
  // tools only). Public visitors never see them; admins authenticated via /admin do.
  const isAdmin = typeof window !== 'undefined' && !!localStorage.getItem('adminPassword');
  const brochureRef = useRef(null);
  const shareMenuRef = useRef(null);

  // Close share menu on outside click
  useEffect(() => {
    if (!shareOpen) return;
    const onClick = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [shareOpen]);

  // For sharing on social, point to the backend /api/share/deals/{slug} endpoint
  // — that endpoint serves per-deal OG tags (so each LinkedIn/WhatsApp/X preview shows
  // the correct title + property image) and instantly redirects humans to /deals/{slug}.
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/share/deals/${slug}`
    : '';
  // The actual page URL (used as the canonical link displayed under previews)
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/deals/${slug}`
    : '';
  const buildShareText = () => {
    if (!deal) return '';
    const title = (deal.title || '').replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    const fee = deal.finders_fee ? `Founders Fee: ${deal.finders_fee_currency === 'GBP' ? '£' : (deal.finders_fee_currency === 'EUR' ? '€' : 'US$')}${Number(deal.finders_fee).toLocaleString()}` : '';
    const loc = deal.location ? `Location: ${deal.location}` : '';
    return [title, fee, loc, '', shareUrl].filter(Boolean).join('\n');
  };

  const openShare = async (platform) => {
    const plainText = buildShareText();
    const text = encodeURIComponent(plainText);
    const url = encodeURIComponent(shareUrl);
    const titleEnc = encodeURIComponent((deal.title || 'Investment Opportunity').replace(/\n/g, ' '));
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

    if (platform === 'whatsapp') {
      // WhatsApp is unreliable because many corporate networks / browser extensions block
      // wa.me, api.whatsapp.com AND web.whatsapp.com. The most universal solution:
      // copy the prefilled message to the clipboard so the user can paste it into any
      // WhatsApp chat they already have open. We ALSO attempt to open the appropriate URL
      // in case the user's network does allow it.
      try {
        await navigator.clipboard.writeText(plainText);
        toast.success('Message copied! Paste it into any WhatsApp chat.', { duration: 5000 });
      } catch {
        toast.info('Copy this message:', { description: plainText.slice(0, 100) + '…', duration: 8000 });
      }
      try {
        const waUrl = isMobile ? `https://wa.me/?text=${text}` : `https://web.whatsapp.com/send?text=${text}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');
      } catch { /* network may block — clipboard is the fallback */ }
      setShareOpen(false);
      return;
    }

    let target = '';
    if (platform === 'linkedin') target = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    else if (platform === 'email') target = `mailto:?subject=${titleEnc}&body=${text}`;
    else if (platform === 'x') target = `https://twitter.com/intent/tweet?url=${url}&text=${titleEnc}`;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
    setShareOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check redirect mode first — send visitors straight to partner site if enabled
        try {
          const cfgRes = await fetch(`${API_URL}/api/public/deal-sourcing-enabled`);
          if (cfgRes.ok) {
            const cfg = await cfgRes.json();
            if (cfg?.redirect_enabled && cfg?.redirect_url) {
              window.location.replace(cfg.redirect_url);
              return;
            }
          }
        } catch { }
        const [dealRes, sigRes] = await Promise.all([
          fetch(`${API_URL}/api/public/deals/${slug}`),
          fetch(`${API_URL}/api/public/signature-settings`).catch(() => null),
        ]);
        if (dealRes.ok) {
          const d = await dealRes.json();
          if (sigRes && sigRes.ok) {
            const sig = await sigRes.json();
            d._logo_url = sig.logo_url || '';
          }
          setDeal(d);
          // Fire-and-forget view tracking (30-min server-side dedupe per IP+UA)
          fetch(`${API_URL}/api/public/deals/${slug}/track-view`, {
            method: 'POST',
            keepalive: true,
          }).catch(() => {});
        }
      } catch { }
      setLoading(false);
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  // Convert a cross-origin image URL to a base64 data URL via fetch — bypasses CORS canvas tainting.
  const urlToDataUrl = async (url) => {
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };

  const captureCanvas = async () => {
    if (!brochureRef.current) return null;
    // 1) Pre-inline every <img> in the brochure as a base64 data URL so html2canvas
    //    can render them without any CORS tainting (the #1 cause of "missing image" issues).
    try {
      const imgs = Array.from(brochureRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map(async (img) => {
        if (!img.src || img.src.startsWith('data:')) return;
        const dataUrl = await urlToDataUrl(img.src);
        if (dataUrl) img.src = dataUrl;
      }));
    } catch { /* ignore */ }

    // 2) Wait for fonts + (now-inlined) images to be fully ready
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const imgs = Array.from(brochureRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(resolve => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 4000);
        });
      }));
    } catch { /* ignore */ }

    return await html2canvas(brochureRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#f4eddc',
      logging: false,
      windowWidth: brochureRef.current.scrollWidth,
      imageTimeout: 8000,
    });
  };

  const downloadPNG = async () => {
    setDownloading('png');
    try {
      const canvas = await captureCanvas();
      if (!canvas) throw new Error('capture failed');
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('blob conversion failed');
      const objUrl = URL.createObjectURL(blob);
      // Trigger the download
      const link = document.createElement('a');
      link.download = `${slug || 'deal'}.png`;
      link.href = objUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Show inline preview modal (extensions cannot block this — it's part of our page)
      setPreviewModal({ type: 'png', url: objUrl, filename: `${slug || 'deal'}.png` });
      toast.success('PNG downloaded — preview shown below');
    } catch (e) {
      toast.error('Download failed: ' + (e?.message || 'unknown'));
    }
    setDownloading(null);
  };

  const downloadPDF = async () => {
    setDownloading('pdf');
    try {
      const canvas = await captureCanvas();
      if (!canvas) throw new Error('capture failed');
      const imgData = canvas.toDataURL('image/png');

      // Build a PDF whose ONE page matches the brochure's exact aspect ratio.
      // This guarantees PDF, PNG, and the in-page preview all look identical — no A4 cropping,
      // no multi-page splits, no scaling distortion. Width is fixed at 210 mm (A4 portrait width)
      // so the document still prints cleanly on standard paper if needed.
      const widthMM = 210;
      const heightMM = (canvas.height / canvas.width) * widthMM;
      const pdf = new jsPDF({
        orientation: heightMM > widthMM ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [widthMM, heightMM],
        compress: true,
      });
      pdf.addImage(imgData, 'PNG', 0, 0, widthMM, heightMM, undefined, 'FAST');

      const filename = `${slug || 'deal'}.pdf`;
      pdf.save(filename);
      const pdfBlob = pdf.output('blob');
      setPreviewModal({ type: 'pdf', url: imgData, filename, pdfBlob });
      toast.success('PDF downloaded — preview shown below');
    } catch (e) {
      toast.error('Download failed: ' + (e?.message || 'unknown'));
    }
    setDownloading(null);
  };

  const reDownloadFromPreview = () => {
    if (!previewModal) return;
    const link = document.createElement('a');
    link.download = previewModal.filename;
    if (previewModal.type === 'pdf' && previewModal.pdfBlob) {
      link.href = URL.createObjectURL(previewModal.pdfBlob);
    } else {
      link.href = previewModal.url;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Re-downloaded');
  };

  if (loading) return <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!deal) return <div className="min-h-screen bg-[#0a1a14] flex flex-col items-center justify-center text-white"><h2 className="text-2xl font-bold mb-4">Deal Not Found</h2><Link to="/deals" className="text-[#D4AF37] hover:underline">Browse all deals</Link></div>;

  const detailCards = deal.detail_cards || [];
  const pillars = deal.pillars || [];
  const featureBadges = deal.feature_badges || [];
  const gallery = Array.isArray(deal.gallery) ? deal.gallery : [];
  const returnsStrip = Array.isArray(deal.returns_strip) ? deal.returns_strip : [];
  const currency = deal.finders_fee_currency || 'USD';
  const currencyPrefix = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : 'US$';
  // Title respects \n for forced line breaks. If the title has no \n but contains pipes,
  // we auto-break into 3 visual lines so production data without manual breaks still
  // renders the intended 1/2/2 pipe-chunk layout instead of running into the photo wedge.
  const rawTitle = (deal.title || '').trim();
  const buildTitleLines = (raw) => {
    if (raw.includes('\n')) return raw.split('\n').map(l => l.trim()).filter(Boolean);
    const chunks = raw.split('|').map(s => s.trim()).filter(Boolean);
    if (chunks.length <= 1) return [raw];
    if (chunks.length === 2) return [chunks[0] + ' |', chunks[1]];
    if (chunks.length === 3) return [chunks[0] + ' |', chunks[1] + ' |', chunks[2]];
    if (chunks.length === 4) return [chunks[0] + ' |', chunks[1] + ' | ' + chunks[2] + ' |', chunks[3]];
    // 5+ chunks → first alone, then pair, then remaining
    return [
      chunks[0] + ' |',
      chunks[1] + ' | ' + chunks[2] + ' |',
      chunks.slice(3).join(' | '),
    ];
  };
  const titleLines = buildTitleLines(rawTitle);
  // Company logo (fetched from signature_settings on mount, see effect below)
  const logoUrl = deal._logo_url;

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }}>
      <Toaster theme="dark" />

      {/* Download Toolbar */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-6 pb-3 flex items-center justify-between gap-3">
        <Link to="/deals" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold transition-colors hover:opacity-80" style={{ color: GREEN }} data-testid="back-to-deals-top">
          <ArrowLeft className="w-3.5 h-3.5" /> All Opportunities
        </Link>
        <div className="flex items-center gap-2 relative">
          {/* One-tap WhatsApp share — admin-only (private distribution tool, never shown to public visitors) */}
          {isAdmin && (
          <button
            onClick={() => {
              const { direct } = buildWhatsAppCaptions(deal, shareUrl);
              const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
              const text = encodeURIComponent(direct || shareUrl);
              const waUrl = isMobile
                ? `https://wa.me/?text=${text}`
                : `https://web.whatsapp.com/send?text=${text}`;
              window.open(waUrl, '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider rounded transition-all hover:shadow shadow-sm"
            style={{ backgroundColor: '#25D366', color: 'white' }}
            title="Share this deal on WhatsApp (opens WhatsApp with caption + brochure link pre-filled)"
            data-testid="whatsapp-quick-share-btn"
          >
            <WhatsAppIcon /> WhatsApp
          </button>
          )}
          {/* Multi-channel share menu — admin-only */}
          {isAdmin && (
          <div className="relative" ref={shareMenuRef}>
            <button onClick={() => setShareOpen(!shareOpen)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider rounded border-2 transition-all hover:shadow"
              style={{ borderColor: GREEN, color: GREEN, backgroundColor: 'white' }}
              data-testid="share-btn">
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
            {shareOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-md shadow-2xl border z-30 overflow-hidden"
                style={{ borderColor: 'rgba(10,26,20,0.15)' }}
                data-testid="share-menu">
                <ShareItem label="WhatsApp" onClick={() => { setShareOpen(false); setCaptionModal('whatsapp'); }} icon={<WhatsAppIcon />} testid="share-whatsapp" />
                <ShareItem label="Telegram" onClick={() => { setShareOpen(false); setCaptionModal('telegram'); }} icon={<TelegramIcon />} testid="share-telegram" />
                <ShareItem label="LinkedIn" onClick={() => { setShareOpen(false); setCaptionModal('linkedin'); }} icon={<LinkedInIcon />} testid="share-linkedin" />
                <ShareItem label="X (Twitter)" onClick={() => { setShareOpen(false); setCaptionModal('x'); }} icon={<XIcon />} testid="share-x" />
                <ShareItem label="Email" onClick={() => { setShareOpen(false); setCaptionModal('email'); }} icon={<FileText className="w-4 h-4" />} testid="share-email" />
                <div className="border-t" style={{ borderColor: 'rgba(10,26,20,0.1)' }} />
                <ShareItem label="Copy Link" onClick={copyLink} icon={<Link2 className="w-4 h-4" />} testid="share-copy" />
              </div>
            )}
          </div>
          )}
          <button onClick={downloadPNG} disabled={downloading !== null}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider rounded border-2 transition-all hover:shadow disabled:opacity-50"
            style={{ borderColor: GREEN, color: GREEN, backgroundColor: 'white' }}
            data-testid="download-png-btn">
            <FileImage className="w-3.5 h-3.5" /> {downloading === 'png' ? 'Generating…' : 'Download PNG'}
          </button>
          <button onClick={downloadPDF} disabled={downloading !== null}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider rounded shadow-md transition-all hover:shadow-lg disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: GREEN }}
            data-testid="download-pdf-btn">
            <FileText className="w-3.5 h-3.5" /> {downloading === 'pdf' ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      <div ref={brochureRef} className="max-w-[1100px] mx-auto bg-white shadow-2xl">
        {/* ========== HERO ========== */}
        <div className="relative overflow-hidden border" style={{ backgroundColor: GREEN, borderColor: `${GOLD}40` }}>
          <div className="grid grid-cols-1 lg:grid-cols-5 relative">
            {/* Left: Title + Tags */}
            <div className="lg:col-span-3 px-8 sm:px-12 lg:pr-16 py-8 lg:py-12 relative z-10 flex flex-col justify-between">
              <Link to="/deals" className="inline-flex items-center gap-1.5 text-[#D4AF37]/70 hover:text-[#D4AF37] text-[11px] uppercase tracking-widest mb-5 transition-colors font-semibold" data-testid="back-to-deals">
                <ArrowLeft className="w-3.5 h-3.5" /> All Opportunities
              </Link>
              <div className="flex-1 flex flex-col justify-center">
                <h1 className="font-black leading-[1.25] tracking-tight uppercase break-words" style={{ fontSize: 'clamp(0.9rem, 1.35vw, 1.2rem)', wordSpacing: '0.05em' }} data-testid="deal-title">
                  {titleLines.length > 0 ? titleLines.map((line, i) => (
                    <div
                      key={i}
                      className="mb-1"
                      style={{
                        color: i === 0 ? GOLD : '#FFFFFF',
                        fontSize: i === 0 ? '1.25em' : '1em',
                        letterSpacing: i === 0 ? '0.02em' : 'normal',
                      }}
                    >
                      {renderWithGoldPipes(line)}
                    </div>
                  )) : <span style={{ color: GOLD }}>{renderWithGoldPipes(deal.title)}</span>}
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 mt-6">
                {(deal.strategy_tags || []).map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold tracking-wider uppercase rounded shadow-md"
                    style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: GREEN }}
                    data-testid={`tag-${i}`}>
                    <Building2 className="w-3.5 h-3.5" />
                    {tag}
                  </span>
                ))}
                {deal.deal_id && (
                  <span className="inline-flex items-center px-3.5 py-2 text-[11px] font-bold tracking-wider uppercase rounded shadow-md"
                    style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: GREEN }}>
                    DEAL ID: {deal.deal_id}
                  </span>
                )}
              </div>
            </div>

            {/* Right: Property Photo with diagonal dark-green wedge + thin gold line (html2canvas-safe) */}
            <div className="lg:col-span-2 relative min-h-[260px] lg:min-h-[340px] overflow-hidden">
              {deal.hero_image ? (
                <img src={deal.hero_image} alt={deal.title} className="w-full h-full object-cover absolute inset-0" />
              ) : (
                <div className="w-full h-full flex items-center justify-center absolute inset-0"
                  style={{ background: `linear-gradient(135deg, ${GREEN_DEEP} 0%, ${GREEN} 100%)` }}>
                  <div className="text-center opacity-40">
                    <Building2 className="w-20 h-20 mx-auto mb-2" style={{ color: GOLD }} />
                    <span className="text-white/40 text-xs uppercase tracking-widest">Property Photo</span>
                  </div>
                </div>
              )}
              {/* Dark-green diagonal wedge over the left side of photo — creates the "clipped" effect */}
              <div className="hidden lg:block absolute inset-0 pointer-events-none"
                style={{ background: `linear-gradient(110deg, ${GREEN} 0%, ${GREEN} 16.5%, transparent 18.2%, transparent 100%)` }} />
              {/* Thin gold diagonal line traces the cut edge */}
              <div className="hidden lg:block absolute inset-0 pointer-events-none"
                style={{ background: `linear-gradient(110deg, transparent 16.6%, ${GOLD} 16.9%, ${GOLD} 17.4%, transparent 17.7%)` }} />
              {/* Mobile: simple gold strip at top */}
              <div className="lg:hidden h-0.5 absolute top-0 left-0 right-0" style={{ backgroundColor: GOLD }} />
            </div>
          </div>
          {/* Bottom gold strip */}
          <div className="h-1" style={{ background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)` }} />
        </div>

        {/* ========== KEY FACTS + FEATURE BADGES (Light Panel) ========== */}
        <div style={{ backgroundColor: CREAM }}>
          <div className={`px-8 sm:px-12 py-8 grid gap-8 ${featureBadges.length > 0 ? 'lg:grid-cols-2' : 'grid-cols-1'} items-center`}>
            <div className="space-y-5">
              {(deal.finders_fee > 0 || deal.finders_fee === 0) && (
                <FactRow icon={<DollarSign className="w-5 h-5 text-white" />}
                  label="Founders Fee:"
                  value={<span style={{ color: GOLD }} className="font-extrabold">{currencyPrefix}{Number(deal.finders_fee || 0).toLocaleString()}</span>}
                />
              )}
              {deal.location && (
                <FactRow icon={<MapPin className="w-5 h-5 text-white" />} label="Location:" value={deal.location} lighter />
              )}
              {deal.property_type && (
                <FactRow icon={<Building2 className="w-5 h-5 text-white" />} label="Asset Type:" value={deal.property_type} lighter />
              )}
              {deal.positioning && (
                <FactRow icon={<Star className="w-5 h-5 text-white" />} label="Positioning:" value={renderWithGoldPipes(deal.positioning)} lighter />
              )}
            </div>

            {featureBadges.length > 0 && (
              <div className="grid grid-cols-3 gap-x-3 gap-y-5 lg:border-l lg:pl-8" style={{ borderColor: 'rgba(10,26,20,0.12)' }}>
                {featureBadges.map((b, i) => (
                  <div key={i} className="flex flex-col items-center text-center px-1" data-testid={`feature-badge-${i}`}>
                    <div className="mb-2 flex items-center justify-center" style={{ color: GOLD }}>
                      {getIcon(b.icon || 'check', 'w-7 h-7')}
                    </div>
                    <p className="text-[11px] font-semibold leading-tight" style={{ color: GREEN }}>
                      {b.title}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gold separator */}
          <div className="h-px mx-8 sm:mx-12" style={{ backgroundColor: GOLD, opacity: 0.4 }} />

          {/* Urgency + CTA */}
          <div className="px-8 sm:px-12 py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1 max-w-xl">
              {deal.urgency_text && <p className="font-bold text-base" style={{ color: CREAM_DARK }}>{deal.urgency_text}</p>}
              {deal.urgency_subtext && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: CREAM_DARK, opacity: 0.6 }}>{deal.urgency_subtext}</p>}
            </div>
            <button onClick={() => setShowEnquiry(true)}
              className="flex items-center gap-2.5 px-7 py-4 rounded font-bold text-sm tracking-wide whitespace-nowrap shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
              style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: GREEN }}
              data-testid="deal-cta-btn">
              <span className="text-lg leading-none">+</span> {deal.cta_button_text || 'Access This Opportunity'}
            </button>
          </div>
        </div>

        {/* ========== RETURNS STRIP (Bloomberg-style ledger) ========== */}
        {returnsStrip.length > 0 && (
          <>
            <div className="relative overflow-hidden" style={{ backgroundColor: GREEN_DEEP }}>
              {/* Gold corner accents — rotated divs instead of clip-path polygons,
                  because html2canvas does NOT render clip-path: polygon() reliably. */}
              <div
                className="hidden lg:block absolute pointer-events-none"
                style={{
                  top: '-30%',
                  left: '-40px',
                  width: '110px',
                  height: '160%',
                  background: `linear-gradient(135deg, ${GOLD}55 0%, transparent 70%)`,
                  transform: 'rotate(15deg)',
                  transformOrigin: 'center',
                }}
                aria-hidden
              />
              <div
                className="hidden lg:block absolute pointer-events-none"
                style={{
                  top: '-30%',
                  right: '-40px',
                  width: '110px',
                  height: '160%',
                  background: `linear-gradient(225deg, ${GOLD}55 0%, transparent 70%)`,
                  transform: 'rotate(-15deg)',
                  transformOrigin: 'center',
                }}
                aria-hidden
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 relative z-10">
                {returnsStrip.map((cell, i) => (
                  <div key={i}
                    className={`px-4 lg:px-5 py-7 lg:py-8 text-center relative ${
                      i < returnsStrip.length - 1
                        ? 'lg:after:absolute lg:after:right-0 lg:after:top-1/2 lg:after:-translate-y-1/2 lg:after:h-[60%] lg:after:w-px lg:after:opacity-40'
                        : ''
                    }`}
                    style={i < returnsStrip.length - 1 ? { '--after-bg': GOLD } : {}}
                    data-testid={`returns-cell-${i}`}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2.5"
                      style={{ color: GOLD, opacity: 0.85 }}>
                      {cell.label}
                    </p>
                    <p className="font-black leading-[1.05] tracking-tight tabular-nums"
                      style={{ color: CREAM, fontSize: 'clamp(1.15rem, 1.5vw, 1.55rem)' }}>
                      {cell.value}
                    </p>
                    {cell.caption && (
                      <p className="text-[10px] italic mt-1.5" style={{ color: 'rgba(244,237,220,0.55)' }}>
                        {cell.caption}
                      </p>
                    )}
                    {/* Vertical gold divider — pseudo-style via inline element for cross-browser */}
                    {i < returnsStrip.length - 1 && (
                      <span className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-[55%] w-px"
                        style={{ background: GOLD, opacity: 0.35 }} aria-hidden />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer on cream body */}
            {deal.returns_disclaimer && (
              <div style={{ backgroundColor: CREAM }}>
                <p className="px-8 sm:px-12 pt-5 pb-2 text-[10.5px] italic text-center max-w-[760px] mx-auto leading-relaxed"
                  style={{ color: GREEN, opacity: 0.55 }} data-testid="returns-disclaimer">
                  {deal.returns_disclaimer}
                </p>
              </div>
            )}
          </>
        )}

        {/* ========== HEADLINE DEAL DETAILS ========== */}
        {(detailCards.length > 0 || deal.headline_subtitle) && (
          <div style={{ backgroundColor: CREAM }}>
            <div className="px-8 sm:px-12 pt-2 pb-8">
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-5 pt-4 border-t-2" style={{ borderColor: GOLD }}>
                <Building2 className="w-6 h-6" style={{ color: GREEN }} />
                <h2 className="font-black text-xl sm:text-2xl tracking-tight uppercase" style={{ color: GREEN }}>
                  {deal.headline_title || 'HEADLINE DEAL DETAILS'}
                </h2>
              </div>

              {/* Gold subtitle banner */}
              {deal.headline_subtitle && (
                <div className="rounded mb-6 px-5 py-3.5 shadow-md" style={{ background: GREEN }}>
                  <p className="font-bold text-base tracking-wide" style={{ color: GOLD }}>{deal.headline_subtitle}</p>
                </div>
              )}

              {/* Detail Cards Grid - 3 columns × 2 rows */}
              {detailCards.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
                  {detailCards.map((card, i) => {
                    // Payment Structure & Investor Highlights → use check icons instead of dots
                    const useCheckBullets = ['credit-card', 'target'].includes(card.icon);
                    return (
                    <div key={i} data-testid={`detail-card-${i}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: GREEN }}>
                          {getIcon(card.icon || 'info', 'w-4 h-4', GOLD)}
                        </div>
                        <h3 className="font-extrabold text-sm tracking-wider uppercase" style={{ color: GOLD }}>{card.title}</h3>
                      </div>

                      {card.highlight && (
                        <div className="mb-4 grid grid-cols-2 rounded-lg overflow-hidden shadow-md">
                          {(card.highlight.split('|').length === 2 ? card.highlight.split('|') : [card.highlight, '']).map((seg, k) => {
                            const isGold = k === 0;
                            return (
                              <div key={k}
                                className="px-3 py-3 text-center flex items-center justify-center"
                                style={{
                                  background: isGold
                                    ? `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`
                                    : GREEN,
                                  borderRight: isGold ? `1px solid ${GOLD_DEEP}` : 'none',
                                }}>
                                <p className="text-[10px] leading-tight font-extrabold uppercase tracking-[0.08em]"
                                  style={{ color: isGold ? GREEN : GOLD }}>
                                  {seg.trim()}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <ul className={`pl-1 ${card.icon === 'gem' ? 'grid grid-cols-1 gap-y-1.5' : 'space-y-1.5'}`}>
                        {(card.items || []).map((item, j) => {
                          const itemText = typeof item === 'string' ? item : (item.text || '');
                          const itemIcon = typeof item === 'object' && item.icon ? item.icon : null;
                          const isFootnote = typeof item === 'string' && item.startsWith('*') && item.endsWith('*') && item.length > 2;
                          const text = isFootnote ? itemText.slice(1, -1) : itemText;
                          if (isFootnote) {
                            return (
                              <li key={j} className="mt-3 mb-1 pl-3 pr-2 py-2 rounded-sm border-l-[3px]"
                                style={{ borderColor: GOLD, background: 'rgba(212,175,55,0.08)' }}>
                                <p className="text-[12px] font-bold leading-snug tracking-wide" style={{ color: GREEN }}>
                                  {text}
                                </p>
                              </li>
                            );
                          }
                          return (
                            <li key={j} className="flex items-start gap-2 text-[13px] leading-snug" style={{ color: GREEN }}>
                              {itemIcon ? (
                                <span className="mt-0.5 flex-shrink-0" style={{ display: 'inline-flex' }}>
                                  {getIcon(itemIcon, 'w-3.5 h-3.5', GOLD)}
                                </span>
                              ) : useCheckBullets ? (
                                <Check className="mt-0.5 w-3.5 h-3.5 flex-shrink-0" style={{ color: GOLD }} strokeWidth={3} />
                              ) : (
                                // Plain CSS dot — much more reliable in html2canvas than a tiny SVG
                                <span
                                  className="flex-shrink-0"
                                  style={{
                                    display: 'inline-block',
                                    width: '7px',
                                    height: '7px',
                                    borderRadius: '50%',
                                    backgroundColor: GOLD,
                                    marginTop: '6px',
                                  }}
                                  aria-hidden
                                />
                              )}
                              <span>{text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );})}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Legacy details fallback (old deals) */}
        {(!detailCards.length && deal.details?.length > 0) && (
          <div className="px-8 sm:px-12 py-8" style={{ backgroundColor: CREAM }}>
            <div className="flex items-center gap-3 mb-4 pt-4 border-t-2" style={{ borderColor: GOLD }}>
              <Building2 className="w-6 h-6" style={{ color: GREEN }} />
              <h2 className="font-black text-xl tracking-tight uppercase" style={{ color: GREEN }}>{deal.headline_title || 'Deal Details'}</h2>
            </div>
            <div className="space-y-2">
              {deal.details.map((d, i) => (
                <div key={i} className="flex gap-3">
                  <span className="font-bold text-sm min-w-[140px] flex-shrink-0" style={{ color: GREEN }}>{d.label}:</span>
                  <span className="text-sm" style={{ color: GREEN, opacity: 0.8 }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== 4 PILLARS BANNER ========== */}
        {pillars.length > 0 && (
          <div style={{ backgroundColor: GREEN }}>
            <div className="px-6 sm:px-10 py-7">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-5 gap-x-4">
                {pillars.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 px-2" data-testid={`pillar-${i}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border" style={{ borderColor: GOLD, background: 'rgba(212,175,55,0.08)' }}>
                      {getIcon(p.icon || 'check', 'w-4.5 h-4.5', GOLD)}
                    </div>
                    <p className="text-white font-bold text-[11px] tracking-wider uppercase leading-tight">{p.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== GALLERY ========== */}
        {gallery.length > 0 && (
          <div style={{ backgroundColor: CREAM }}>
            <div className="px-8 sm:px-12 py-7">
              <div className="flex items-center gap-3 mb-5">
                <FileImage className="w-5 h-5" style={{ color: GREEN }} />
                <h2 className="font-black text-base sm:text-lg tracking-tight uppercase" style={{ color: GREEN }}>
                  Property Gallery
                </h2>
                <span className="text-xs font-medium" style={{ color: GREEN, opacity: 0.5 }}>({gallery.length})</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {gallery.map((url, i) => (
                  <button key={i} onClick={() => setLightboxIdx(i)}
                    className="relative group overflow-hidden rounded-md shadow-sm hover:shadow-lg transition-all"
                    style={{ aspectRatio: '4/3' }}
                    data-testid={`gallery-${i}`}>
                    <img src={url} alt={`Property ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" crossOrigin="anonymous" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" style={{ background: 'rgba(10,26,20,0.5)' }}>
                      <Eye className="w-5 h-5" style={{ color: GOLD }} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== FOOTER SUMMARY ========== */}
        <div style={{ backgroundColor: CREAM }}>
          <div className="px-8 sm:px-12 py-6 flex flex-col sm:flex-row items-start gap-8 justify-between">
            {deal.summary && (
              <div className="flex items-start gap-3 flex-1">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: GREEN }}>
                  <Building2 className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <p className="text-[13px] leading-relaxed" style={{ color: GREEN, opacity: 0.85 }}>{deal.summary}</p>
              </div>
            )}
            {deal.disclosure_text && (
              <div className="flex items-start gap-3 flex-shrink-0 max-w-[260px]">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: GREEN }}>
                  <Lock className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <p className="text-[12px] leading-relaxed font-semibold" style={{ color: GREEN, opacity: 0.85 }}>{deal.disclosure_text}</p>
              </div>
            )}
          </div>
          {/* Brand Footer */}
          <div className="px-8 sm:px-12 py-4 flex items-center justify-between border-t" style={{ borderColor: 'rgba(10,26,20,0.1)' }}>
            <div className="flex items-center gap-2.5">
              {logoUrl && (
                <img src={logoUrl} alt="Black Star PI" className="h-7 w-auto object-contain" crossOrigin="anonymous" />
              )}
              <span className="font-bold text-xs tracking-widest" style={{ color: GOLD }}>BLACK STAR PI</span>
            </div>
            <span className="text-[10px]" style={{ color: GREEN, opacity: 0.4 }}>Confidential Investment Opportunity</span>
          </div>
        </div>
      </div>

      {showEnquiry && <EnquiryModal deal={deal} onClose={() => setShowEnquiry(false)} />}
      {captionModal && <ShareCaptionModal platform={captionModal} deal={deal} shareUrl={shareUrl} onClose={() => setCaptionModal(null)} />}

      {/* Gallery Lightbox */}
      {lightboxIdx !== null && gallery[lightboxIdx] && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightboxIdx(null)} data-testid="gallery-lightbox">
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-white/80 hover:text-white z-10"
            data-testid="lightbox-close"><X className="w-5 h-5" /></button>
          {lightboxIdx > 0 && (
            <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full hover:bg-white/10 text-white/80 hover:text-white z-10"
              data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
          )}
          {lightboxIdx < gallery.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full hover:bg-white/10 text-white/80 hover:text-white z-10"
              data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
          )}
          <img src={gallery[lightboxIdx]} alt={`Property ${lightboxIdx + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl"
            onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 text-white/70 text-xs font-medium">
            {lightboxIdx + 1} / {gallery.length}
          </div>
        </div>
      )}

      {/* Inline Preview Modal (PNG/PDF) — renders within page so ad-blockers can't intercept */}
      {previewModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => { URL.revokeObjectURL(previewModal.url); setPreviewModal(null); }}
          data-testid="preview-modal">
          <div className="bg-[#0a1a14] border border-[#D4AF37]/30 rounded-xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#D4AF37]/20 bg-[#0a1a14]">
              <div className="flex items-center gap-3">
                {previewModal.type === 'png' ? <FileImage className="w-4 h-4 text-[#D4AF37]" /> : <FileText className="w-4 h-4 text-[#D4AF37]" />}
                <div>
                  <p className="text-[#D4AF37] text-xs font-bold tracking-widest uppercase">{previewModal.type} Preview</p>
                  <p className="text-white/40 text-[10px]">{previewModal.filename}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={previewModal.type === 'pdf' && previewModal.pdfBlob ? URL.createObjectURL(previewModal.pdfBlob) : previewModal.url}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-colors"
                  data-testid="preview-open-new-tab">
                  <ExternalLink className="w-3 h-3" /> Open in new tab
                </a>
                <button onClick={reDownloadFromPreview}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="preview-redownload-btn">
                  <Download className="w-3 h-3" /> Save Again
                </button>
                <button onClick={() => { URL.revokeObjectURL(previewModal.url); setPreviewModal(null); }}
                  className="p-1.5 rounded hover:bg-white/5 text-white/40 hover:text-white" data-testid="preview-close-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-auto bg-black/40 flex items-start justify-center p-4">
              <img src={previewModal.url} alt="Brochure preview" className="max-w-full shadow-2xl" style={{ maxHeight: '76vh', objectFit: 'contain' }} />
            </div>
            <div className="px-5 py-3 border-t border-[#D4AF37]/20 bg-[#0a1a14] text-center">
              <p className="text-white/30 text-[10px]">File saved to your Downloads folder. If you don't see it, check your browser's download history (Ctrl+J / Cmd+J).</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Sub-components ==============

// ===== Shared deal-fact extractor — used by all platform caption builders =====
const extractDealFacts = (deal, shareUrl) => {
  if (!deal) return null;
  const rawTitle = (deal.title || '').replace(/\n/g, ' ').replace(/\s*\|\s*/g, ' | ').replace(/\s+/g, ' ').trim();
  const titleChunks = rawTitle.split('|').map(s => s.trim()).filter(Boolean);
  const headline = titleChunks[0] || rawTitle || 'Private Investment Opportunity';
  const propertyLine = titleChunks.slice(1)
    .filter(c => !/^from\s+(us\$|usd|gbp|£|€)/i.test(c))
    .map(c => c.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()))
    .join(' · ');
  const priceMatch = rawTitle.match(/from\s+((?:us\$|usd|gbp|£|€|us\$)\s*[\d,.]+\s*\+?)/i);
  const entryPrice = priceMatch ? priceMatch[1].toUpperCase().replace(/US\$/i, 'USD ').replace(/\s+/g, ' ').trim() : '';
  const feeCurrency = deal.finders_fee_currency === 'GBP' ? '£' : (deal.finders_fee_currency === 'EUR' ? '€' : 'US$');
  const fee = deal.finders_fee ? `${feeCurrency}${Number(deal.finders_fee).toLocaleString()}` : '';
  const location = (deal.location || '').replace(/\s+/g, ' ').trim();
  const locShort = location ? location.split(',')[0].trim() : '';
  const assetType = (deal.asset_type || '').trim();

  // 4 best bullets from detail_cards
  const detailCards = Array.isArray(deal.detail_cards) ? deal.detail_cards : [];
  let bullets = [];
  for (const card of detailCards) {
    if (Array.isArray(card.items)) {
      for (const item of card.items) {
        if (typeof item === 'string' && item.trim() && bullets.length < 4) bullets.push(item.trim());
      }
    }
    if (bullets.length >= 4) break;
  }
  if (bullets.length === 0 && Array.isArray(deal.pillars)) {
    bullets = deal.pillars.slice(0, 4).map(p => p.title || p.label).filter(Boolean);
  }

  // Hashtags
  const baseTags = ['#PropertyInvestment', '#RealEstate', '#BlackStarPI'];
  const dynamicTags = (deal.strategy_tags || []).map(t => {
    const titled = String(t).toLowerCase().split(/[\s_-]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
    return '#' + titled.replace(/[^a-zA-Z0-9]/g, '');
  }).filter(t => t.length > 1);
  if (location.toLowerCase().includes('ghana') || location.toLowerCase().includes('accra')) {
    dynamicTags.push('#Ghana', '#AccraProperty', '#InvestInAfrica');
  } else if (location.toLowerCase().includes('uk') || location.toLowerCase().includes('london')) {
    dynamicTags.push('#UKProperty', '#LondonRealEstate');
  }
  const hashtags = Array.from(new Set([...dynamicTags, ...baseTags])).slice(0, 8).join(' ');
  const shortHashtags = Array.from(new Set([...dynamicTags, ...baseTags])).slice(0, 3).join(' ');

  return { rawTitle, headline, propertyLine, entryPrice, fee, location, locShort, assetType, bullets, hashtags, shortHashtags, shareUrl };
};

// ===== LinkedIn captions (long-form, 3000 char limit) =====
const buildLinkedInCaptions = (deal, shareUrl) => {
  const f = extractDealFacts(deal, shareUrl);
  if (!f) return { investor: '', short: '', conversational: '' };
  const { propertyLine, entryPrice, fee, locShort, assetType, bullets, hashtags, headline } = f;

  const investor = [
    `**Private opportunity now open${locShort ? ` — ${locShort}` : ''}.**`,
    '',
    assetType
      ? `A ${assetType.toLowerCase()}${propertyLine ? ` — ${propertyLine.toLowerCase()}` : ''}.`
      : (propertyLine ? `${propertyLine}.` : ''),
    '',
    ...bullets.slice(0, 4).map(b => `→ ${b}`),
    '',
    [entryPrice && `From ${entryPrice}.`, fee && `Reservation: ${fee}.`].filter(Boolean).join('  ·  '),
    '',
    'Full investor brochure on request. Link below for direct access.',
    '',
    shareUrl,
    '',
    hashtags,
  ].join('\n').replace(/\n{3,}/g, '\n\n');

  const short = [
    `🌍 OFF-MARKET${locShort ? ` | ${locShort.toUpperCase()}` : ''}`,
    '',
    propertyLine ? `${propertyLine}.` : (assetType || headline),
    entryPrice ? `Entry from ${entryPrice}${fee ? ` — reservation just ${fee}.` : '.'}` : (fee ? `Reservation just ${fee}.` : ''),
    '',
    'Curated for serious investors. Limited inventory.',
    '',
    '🔗 Full briefing & live brochure ↓',
    shareUrl,
    '',
    hashtags,
  ].filter(Boolean).join('\n');

  const conv = [
    `Just released into our private investor circle 👇`,
    '',
    `A ${assetType ? assetType.toLowerCase() : 'premium property opportunity'}${locShort ? ` in ${locShort}` : ''} — one of those rare deals where the location, the pricing window, and the build quality all line up.`,
    '',
    [entryPrice && `Entry from **${entryPrice}**`, fee && `reservation just **${fee}**`].filter(Boolean).join(', ') + '.',
    '',
    "If you're building exposure to this market — or you've been waiting for the right deal — this is worth 5 minutes of your time.",
    '',
    'Brochure + full numbers at the link 👇',
    shareUrl,
    '',
    hashtags,
  ].join('\n').replace(/\n{3,}/g, '\n\n');

  return { investor, short, conversational: conv };
};

// ===== X / Twitter captions (280 char limit, link counts as 23) =====
const buildTwitterCaptions = (deal, shareUrl) => {
  const f = extractDealFacts(deal, shareUrl);
  if (!f) return { headline: '', teaser: '', thread: '' };
  const { entryPrice, fee, locShort, assetType, shortHashtags, propertyLine } = f;

  // Available chars after link (~23) + url (the actual shareUrl on X counts as 23 always)
  const headline = [
    `🌍 NEW DEAL${locShort ? ` · ${locShort.toUpperCase()}` : ''}`,
    propertyLine || assetType || 'Off-market property opportunity',
    [entryPrice && `From ${entryPrice}`, fee && `Reservation ${fee}`].filter(Boolean).join(' · '),
    shareUrl,
    shortHashtags,
  ].filter(Boolean).join('\n');

  const teaser = [
    `What if you could secure a luxury ${assetType ? assetType.split(' ')[0].toLowerCase() : 'property'} deal${locShort ? ` in ${locShort}` : ''} for just ${fee || 'a small reservation'}? 👇`,
    entryPrice ? `Entry from ${entryPrice}.` : '',
    shareUrl,
    shortHashtags,
  ].filter(Boolean).join('\n');

  const thread = [
    `🧵 New off-market deal${locShort ? ` in ${locShort}` : ''}.`,
    `Why we like it:`,
    `→ ${entryPrice ? `Entry ${entryPrice}` : 'Strong pricing'}`,
    `→ ${fee ? `${fee} secures it` : 'Low reservation'}`,
    `→ Premium location`,
    shareUrl,
  ].filter(Boolean).join('\n');

  return { headline, teaser, thread };
};

// ===== WhatsApp captions (uses *bold* markdown, no length limit) =====
const buildWhatsAppCaptions = (deal, shareUrl) => {
  const f = extractDealFacts(deal, shareUrl);
  if (!f) return { friendly: '', direct: '', detailed: '' };
  const { entryPrice, fee, locShort, assetType, bullets, propertyLine, headline } = f;

  const friendly = [
    `Hey 👋`,
    '',
    `Wanted to share this with you — just came through our private deal pipeline:`,
    '',
    `*${headline}*${propertyLine ? `\n${propertyLine}` : ''}`,
    locShort ? `📍 ${locShort}` : '',
    entryPrice ? `💰 From ${entryPrice}` : '',
    fee ? `🔒 ${fee} secures pricing` : '',
    '',
    `Full details + brochure here:`,
    shareUrl,
    '',
    `Worth a quick look 🙌`,
  ].filter(Boolean).join('\n');

  const direct = [
    `*${headline}*`,
    propertyLine || assetType || '',
    locShort ? `📍 ${locShort}` : '',
    entryPrice ? `From ${entryPrice}` : '',
    '',
    shareUrl,
  ].filter(Boolean).join('\n');

  const detailed = [
    `🌍 *${headline}*`,
    propertyLine ? `_${propertyLine}_` : '',
    '',
    locShort ? `📍 *Location:* ${locShort}` : '',
    assetType ? `🏢 *Asset:* ${assetType}` : '',
    entryPrice ? `💰 *Entry from:* ${entryPrice}` : '',
    fee ? `🔒 *Reservation:* ${fee}` : '',
    '',
    bullets.length ? '*Highlights:*' : '',
    ...bullets.slice(0, 4).map(b => `• ${b}`),
    '',
    `*Full brochure + investor pack:*`,
    shareUrl,
    '',
    `Happy to walk you through it — just reply here.`,
  ].filter(Boolean).join('\n');

  return { friendly, direct, detailed };
};

// ===== Email captions (subject + body, two variants) =====
const buildEmailCaptions = (deal, shareUrl) => {
  const f = extractDealFacts(deal, shareUrl);
  if (!f) return { formal: { subject: '', body: '' }, personal: { subject: '', body: '' } };
  const { entryPrice, fee, locShort, assetType, bullets, propertyLine, headline } = f;

  const formal = {
    subject: `Private Investment Opportunity${locShort ? ` — ${locShort}` : ''}${entryPrice ? ` · From ${entryPrice}` : ''}`,
    body: [
      `Dear [Name],`,
      '',
      `I'd like to share a private opportunity that has just opened to our investor circle.`,
      '',
      `${headline}${propertyLine ? ` — ${propertyLine}` : ''}.`,
      '',
      locShort ? `Location:        ${locShort}` : '',
      assetType ? `Asset Type:      ${assetType}` : '',
      entryPrice ? `Entry From:      ${entryPrice}` : '',
      fee ? `Reservation:     ${fee} (secures pricing)` : '',
      '',
      bullets.length ? 'Key Highlights:' : '',
      ...bullets.slice(0, 4).map(b => `  •  ${b}`),
      '',
      `The full investor brochure, financials, and reservation process are available here:`,
      shareUrl,
      '',
      `I would be happy to walk you through the deal in a 15-minute call. Please reply to this email or use the link above to register interest directly.`,
      '',
      `Best regards,`,
      `Black Star PI`,
    ].filter(Boolean).join('\n'),
  };

  const personal = {
    subject: `Thought of you — ${headline}`,
    body: [
      `Hi [Name],`,
      '',
      `Quick note — we just released a new opportunity I think you'd like:`,
      '',
      `${headline}${locShort ? `, ${locShort}` : ''}.`,
      entryPrice ? `Entry from ${entryPrice}${fee ? `, just ${fee} to secure pricing.` : '.'}` : '',
      '',
      `Full brochure + numbers here:`,
      shareUrl,
      '',
      `Worth a look — happy to chat if any questions.`,
      '',
      `Best,`,
    ].filter(Boolean).join('\n'),
  };

  return { formal, personal };
};

// ===== Telegram captions — reuse WhatsApp content but strip *asterisks*
//       (Telegram's t.me/share/url passes plain text; markdown won't render).
const buildTelegramCaptions = (deal, shareUrl) => {
  const wa = buildWhatsAppCaptions(deal, shareUrl);
  const strip = (s) => (s || '').replace(/\*/g, '');
  return { friendly: strip(wa.friendly), direct: strip(wa.direct), detailed: strip(wa.detailed) };
};

// ===== Generic share caption modal — adapts to platform =====
const PLATFORM_CONFIG = {
  linkedin: {
    name: 'LinkedIn',
    accent: '#0A66C2',
    charLimit: 3000,
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8.3 18.3H5.7v-8h2.6v8zM7 9.2c-.9 0-1.5-.7-1.5-1.4 0-.8.6-1.5 1.5-1.5s1.4.7 1.5 1.5c0 .7-.6 1.4-1.5 1.4zm11.3 9.1h-2.6v-4.4c0-1.2-.5-1.7-1.3-1.7-.8 0-1.4.6-1.4 1.7v4.4h-2.6v-8h2.4v1.1c.4-.6 1.2-1.3 2.5-1.3 1.7 0 3 1.1 3 3.4v4.8z"/></svg>,
    styles: [
      { id: 'investor', label: 'Investor-Grade', sub: 'Best for HNW network' },
      { id: 'short', label: 'Short & Punchy', sub: 'Best for engagement' },
      { id: 'conversational', label: 'Conversational', sub: 'Best for personal feed' },
    ],
    build: buildLinkedInCaptions,
    openShareUrl: (shareUrl) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    helperNote: 'Markdown (**bold**) is for emphasis — LinkedIn keeps them visible (or delete them).',
  },
  x: {
    name: 'X (Twitter)',
    accent: '#000000',
    charLimit: 280,
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    styles: [
      { id: 'headline', label: 'Headline', sub: 'Punchy single tweet' },
      { id: 'teaser', label: 'Teaser', sub: 'Hook question style' },
      { id: 'thread', label: 'Thread Opener', sub: 'Starts a 🧵' },
    ],
    build: buildTwitterCaptions,
    openShareUrl: (shareUrl, caption) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`,
    helperNote: 'X counts a link as ~23 characters. Stay under 280 — counter updates live.',
  },
  whatsapp: {
    name: 'WhatsApp',
    accent: '#25D366',
    charLimit: 4096,
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17.5 14.4l-2.7-.4c-.3 0-.7.1-.9.4l-2 2c-3-1.6-5.6-4.2-7.2-7.2l2-2c.2-.2.3-.6.3-.9l-.4-2.7c-.1-.6-.6-1-1.2-1H3.4c-.7 0-1.3.6-1.3 1.3.4 9.5 8.1 17.2 17.6 17.6.7 0 1.3-.6 1.3-1.3v-1.8c0-.6-.4-1.1-1-1.2z"/></svg>,
    styles: [
      { id: 'friendly', label: 'Friendly', sub: 'Warm + personal' },
      { id: 'direct', label: 'Direct', sub: 'Just the facts' },
      { id: 'detailed', label: 'Detailed Brief', sub: 'Full pitch with bullets' },
    ],
    build: buildWhatsAppCaptions,
    openShareUrl: (shareUrl, caption) => {
      const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
      return isMobile
        ? `https://wa.me/?text=${encodeURIComponent(caption)}`
        : `https://web.whatsapp.com/send?text=${encodeURIComponent(caption)}`;
    },
    helperNote: 'WhatsApp supports *bold*, _italic_, ~strike~. Markdown in this caption will render natively when pasted.',
  },
  email: {
    name: 'Email',
    accent: '#D4AF37',
    charLimit: 5000,
    isEmail: true, // special — separate subject + body
    icon: <FileText className="w-5 h-5" />,
    styles: [
      { id: 'formal', label: 'Formal', sub: 'Professional outreach' },
      { id: 'personal', label: 'Personal', sub: 'Warm intro' },
    ],
    build: buildEmailCaptions,
    openShareUrl: (shareUrl, body, subject) => `mailto:?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`,
    helperNote: "Replace [Name] with the recipient's first name. The 'Open Email' button will pre-fill your mail client.",
  },
  telegram: {
    name: 'Telegram',
    accent: '#229ED9',
    charLimit: 4096,
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>,
    styles: [
      { id: 'friendly', label: 'Friendly', sub: 'Warm + personal' },
      { id: 'direct', label: 'Direct', sub: 'Just the facts' },
      { id: 'detailed', label: 'Detailed Brief', sub: 'Full pitch with bullets' },
    ],
    build: buildTelegramCaptions,
    openShareUrl: (shareUrl, caption) =>
      `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(caption)}`,
    helperNote: 'Telegram opens with the caption pre-filled — pick a contact or channel to forward to.',
  },
};

const ShareCaptionModal = ({ platform, deal, shareUrl, onClose }) => {
  const cfg = PLATFORM_CONFIG[platform];
  const captions = React.useMemo(() => cfg.build(deal, shareUrl), [cfg, deal, shareUrl]);
  const [activeStyle, setActiveStyle] = useState(cfg.styles[0].id);
  // For email: separate subject + body; otherwise just body
  const initialFor = (styleId) => {
    const c = captions[styleId];
    if (cfg.isEmail) return { subject: c.subject || '', body: c.body || '' };
    return { body: typeof c === 'string' ? c : '' };
  };
  const [edited, setEdited] = useState(initialFor(cfg.styles[0].id));
  const [copied, setCopied] = useState(false);
  const lastStyleRef = useRef(cfg.styles[0].id);

  useEffect(() => {
    if (lastStyleRef.current !== activeStyle) {
      setEdited(initialFor(activeStyle));
      lastStyleRef.current = activeStyle;
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStyle, captions]);

  const rawCharCount = (cfg.isEmail ? edited.body : edited.body).length;
  // X treats any URL as 23 chars regardless of length. Mimic that for accurate counting.
  const xEffectiveCount = (() => {
    if (platform !== 'x') return rawCharCount;
    const urlRegex = /https?:\/\/\S+/g;
    let count = edited.body.length;
    const urls = edited.body.match(urlRegex) || [];
    urls.forEach(u => { count -= (u.length - 23); });
    return count;
  })();
  const charCount = platform === 'x' ? xEffectiveCount : rawCharCount;
  const overLimit = charCount > cfg.charLimit;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cfg.isEmail ? edited.body : edited.body);
      setCopied(true);
      toast.success(`Caption copied — ready to paste on ${cfg.name}`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  const handleCopyAndOpen = async () => {
    if (!cfg.isEmail) {
      try {
        await navigator.clipboard.writeText(edited.body);
        toast.success(`Caption copied — opening ${cfg.name}…`);
      } catch { /* ignore */ }
    }
    const target = cfg.isEmail
      ? cfg.openShareUrl(shareUrl, edited.body, edited.subject)
      : cfg.openShareUrl(shareUrl, edited.body);
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={`${platform}-caption-modal`}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0a1a14] border border-[#D4AF37]/25 rounded-2xl shadow-2xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-7 pt-6 pb-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-md"
              style={{ background: `${cfg.accent}22`, color: cfg.accent }}
            >
              {cfg.icon}
            </span>
            <div>
              <h3 className="text-white font-bold text-base leading-tight">{cfg.name} Caption</h3>
              <p className="text-white/40 text-xs mt-0.5">Auto-generated from this deal · pick a style, edit if needed, copy.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5" data-testid={`${platform}-modal-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Style picker */}
        <div className="px-7 pt-5 pb-4">
          <div className={`grid gap-2 ${cfg.styles.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {cfg.styles.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveStyle(s.id)}
                className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                  activeStyle === s.id
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                }`}
                data-testid={`${platform}-style-${s.id}`}
              >
                <div className={`text-xs font-bold ${activeStyle === s.id ? 'text-[#D4AF37]' : 'text-white/80'}`}>{s.label}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{s.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Caption editor */}
        <div className="px-7 pb-5 space-y-3">
          {cfg.isEmail && (
            <div>
              <label className="text-white/60 text-[11px] uppercase tracking-wider font-semibold block mb-1.5">Subject</label>
              <input
                value={edited.subject}
                onChange={e => setEdited({ ...edited, subject: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                data-testid="email-subject"
              />
            </div>
          )}
          <div>
            {cfg.isEmail && <label className="text-white/60 text-[11px] uppercase tracking-wider font-semibold block mb-1.5">Body</label>}
            <textarea
              value={edited.body}
              onChange={e => setEdited({ ...edited, body: e.target.value })}
              rows={cfg.isEmail ? 11 : 13}
              className="w-full bg-black/40 border border-white/10 rounded-lg p-4 text-white/90 text-sm leading-relaxed font-mono focus:outline-none focus:border-[#D4AF37]/40 resize-none whitespace-pre-wrap"
              data-testid={`${platform}-caption-textarea`}
            />
          </div>
          <p className="text-white/30 text-[11px]">
            <span className={overLimit ? 'text-red-400 font-semibold' : ''}>
              {charCount.toLocaleString()} / {cfg.charLimit.toLocaleString()} chars
            </span>
            <span className="text-white/40"> · {cfg.helperNote}</span>
          </p>
        </div>

        {/* Footer actions */}
        <div className="px-7 pb-6 flex items-center justify-end gap-2.5">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors" data-testid={`${platform}-cancel`}>
            Cancel
          </button>
          <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              copied ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-white/5 text-white/80 hover:bg-white/10'
            }`}
            data-testid={`${platform}-copy`}
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy {cfg.isEmail ? 'body' : 'caption'}</>}
          </button>
          <button
            onClick={handleCopyAndOpen}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-bold transition-all bg-gradient-to-r from-[#D4AF37] to-[#B8941E] text-[#0a1a14] hover:shadow-lg hover:shadow-[#D4AF37]/20"
            data-testid={`${platform}-copy-open`}
          >
            {cfg.isEmail ? 'Open Email' : `Copy & Open ${cfg.name}`} <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const ShareItem = ({ label, onClick, icon, testid }) => (
  <button onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12px] font-medium text-left hover:bg-black/5 transition-colors"
    style={{ color: '#0a1a14' }}>
    <span className="flex items-center justify-center w-5 h-5" style={{ color: '#0a1a14' }}>{icon}</span>
    <span>{label}</span>
  </button>
);
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.5 14.4l-2.7-.4c-.3 0-.7.1-.9.4l-2 2c-3-1.6-5.6-4.2-7.2-7.2l2-2c.2-.2.3-.6.3-.9l-.4-2.7c-.1-.6-.6-1-1.2-1H3.4c-.7 0-1.3.6-1.3 1.3.4 9.5 8.1 17.2 17.6 17.6.7 0 1.3-.6 1.3-1.3v-1.8c0-.6-.4-1.1-1-1.2z"/></svg>
);
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8.3 18.3H5.7v-8h2.6v8zM7 9.2c-.9 0-1.5-.7-1.5-1.4 0-.8.6-1.5 1.5-1.5s1.4.7 1.5 1.5c0 .7-.6 1.4-1.5 1.4zm11.3 9.1h-2.6v-4.4c0-1.2-.5-1.7-1.3-1.7-.8 0-1.4.6-1.4 1.7v4.4h-2.6v-8h2.4v1.1c.4-.6 1.2-1.3 2.5-1.3 1.7 0 3 1.1 3 3.4v4.8z"/></svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
);

const FactRow = ({ icon, label, value, lighter }) => (
  <div className="flex items-center gap-4">
    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: GREEN }}>
      {icon}
    </div>
    <div className="flex flex-wrap items-baseline gap-x-2.5">
      <span className="font-bold text-[15px]" style={{ color: CREAM_DARK }}>{label}</span>
      <span className="font-normal text-[15px]" style={{ color: CREAM_DARK, opacity: lighter ? 0.55 : 1 }}>{value}</span>
    </div>
  </div>
);

export default DealPage;
